import { useMemo, memo } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useUtilizationDashboard } from '../../../hooks/analytics/useUtilizationAnalytics';
import { MetricCard } from '../overview/MetricCard';
import { SkeletonLoader } from '../shared/SkeletonLoader';
import { palette } from '../shared/theme';
import { Users, UserPlus, RotateCcw } from 'lucide-react';
import { chartUtils } from '../utils/chartUtils';
import { CurrentMonthLabel } from '../shared/CurrentMonthLabel';
import { METRICS_CONFIG } from '../config';
import { MetricHelpTooltip } from '../shared';

interface UtilizationMetricsProps {
  filters: BaseAnalyticsFilter;
}

export const UtilizationMetrics = memo(({ filters }: UtilizationMetricsProps) => {
  const isMobile = useIsMobile();
  const { data: dashboardData, isLoading } = useUtilizationDashboard(filters);
  const hasEndDateFilter = Boolean(filters?.endDate);

  const kpiItems = useMemo(() => {
    const kpis = dashboardData?.kpis;
    const totalUsers = kpis?.total_users ?? METRICS_CONFIG.DEFAULT_VALUE;
    const newUsersThisMonth = kpis?.new_users_this_month ?? METRICS_CONFIG.DEFAULT_VALUE;
    const returningUsersThisMonth =
      kpis?.returning_users_this_month ?? METRICS_CONFIG.DEFAULT_VALUE;
    const returnUserRate = kpis?.return_user_rate ?? METRICS_CONFIG.DEFAULT_VALUE;
    const newUsersInPeriod = kpis?.new_users_in_period ?? METRICS_CONFIG.DEFAULT_VALUE;
    const returningUsersInPeriod = kpis?.returning_users_in_period ?? METRICS_CONFIG.DEFAULT_VALUE;
    const returnUserRateInPeriod = kpis?.return_user_rate_in_period ?? METRICS_CONFIG.DEFAULT_VALUE;

    const newUsersPrimary = hasEndDateFilter ? newUsersInPeriod : newUsersThisMonth;
    const returningUsersSecondary = hasEndDateFilter
      ? returningUsersInPeriod
      : returningUsersThisMonth;
    const returnRatePrimary = hasEndDateFilter ? returnUserRateInPeriod : returnUserRate;
    const totalUsersLabel = 'Total Members';

    const newUsersLabel = hasEndDateFilter ? (
      'New vs Returning Members (Selected Period)'
    ) : (
      <span>
        New vs Returning Members <CurrentMonthLabel />
      </span>
    );

    const returnRateLabel = hasEndDateFilter ? (
      'Returning Member Rate (Selected Period)'
    ) : (
      <span>
        Returning Member Rate <CurrentMonthLabel />
      </span>
    );

    return [
      {
        id: 'total-users',
        label: totalUsersLabel,
        value: chartUtils.formatNumber(totalUsers),
        secondaryValue: undefined,
        icon: <Users className="h-4 w-4" />,
        color: palette.indigo,
        helpTooltip: (
          <MetricHelpTooltip
            title="What this measures"
            description="Distinct participants who completed at least one session within the selected filters."
          />
        ),
      },
      {
        id: 'new-returning-users',
        label: newUsersLabel,
        value: chartUtils.formatNumber(newUsersPrimary),
        secondaryValue: {
          value: chartUtils.formatNumber(returningUsersSecondary),
          label: undefined,
          color: palette.cyan,
        },
        icon: <UserPlus className="h-4 w-4" />,
        color: palette.cyan,
        helpTooltip: (
          <MetricHelpTooltip
            title="What this measures"
            description="New members are first-time participants in the selected period; the secondary value shows returning members from the same period."
          />
        ),
      },
      {
        id: 'return-rate',
        label: returnRateLabel,
        value: `${returnRatePrimary.toFixed(1)}%`,
        secondaryValue: undefined,
        icon: <RotateCcw className="h-4 w-4" />,
        color: palette.red,
        helpTooltip: (
          <MetricHelpTooltip
            title="What this measures"
            description="Percentage of active members in the selected period who had a session before."
          />
        ),
      },
    ];
  }, [dashboardData, hasEndDateFilter]);

  if (isLoading) {
    return <SkeletonLoader variant="metrics" count={3} />;
  }

  return (
    <div className="space-y-2">
      <div
        className={`grid gap-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-3'}`}
        style={{ ['--anl-metric-bg' as any]: 'var(--anl-surface)' }}
      >
        {kpiItems.map(item => (
          <MetricCard
            key={item.id}
            value={item.value}
            label={item.label}
            secondaryValue={item.secondaryValue?.value}
            secondaryLabel={item.secondaryValue?.label}
            accent={String(item.color)}
            preserveSecondarySpacing
            helpTooltip={item.helpTooltip}
          />
        ))}
      </div>
    </div>
  );
});

UtilizationMetrics.displayName = 'UtilizationMetrics';
